import { Item } from '@ibsys/kit';
import { Tooltip, TruncatedText } from '@/shared/ui';
import { HelpCircleIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColorParameterDefinition } from '@/entities/themes';
import { isFullyTransparent } from '../lib/colorUtils';
import { ColorPickerPopover } from './ColorPickerPopover';

/** Definition + current color values for one parameter (for display in card) */
export type ColorParameterCardItem = ColorParameterDefinition & {
  colorHex: string;
  colorHslH: string;
  colorHslS: string;
  colorHslL: string;
  colorHslA?: string;
};

type ColorParameterCardProps = {
  item: ColorParameterCardItem;
  canEdit?: boolean;
  onColorChange: (hex: string) => void;
};

export const ColorParameterCard: FC<ColorParameterCardProps> = ({ item, canEdit = true, onColorChange }) => {
  const { t } = useTranslation(['themes']);
  const { name, code, description, colorHex, colorHslH, colorHslS, colorHslL, colorHslA } = item;

  const isTransparent = isFullyTransparent(colorHex);
  const hslaString = isTransparent
    ? 'transparent'
    : `hsla(${colorHslH}, ${colorHslS}%, ${colorHslL}%, ${colorHslA ?? '1'})`;

  const colorSquare = (
    <div
      className={`size-12 rounded-md border border-border shrink-0 ${canEdit ? 'cursor-pointer hover:ring-2 hover:ring-primary' : ''}`}
      style={{ backgroundColor: colorHex }}
    />
  );

  return (
    <Item className="flex items-center gap-3" variant="outline">
      <ColorPickerPopover color={colorHex} onColorChange={onColorChange} disabled={!canEdit}>
        {colorSquare}
      </ColorPickerPopover>

      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span className="font-medium text-sm  text-foreground">{name}</span>
          {description && (
            <Tooltip tooltip={description}>
              <HelpCircleIcon className="size-3.5 text-muted-foreground cursor-help shrink-0" />
            </Tooltip>
          )}
        </div>
        <TruncatedText className="text-xs text-muted-foreground">{code}</TruncatedText>
        <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
          <span>{t('themes:colorHexWithValue', { hex: colorHex })}</span>
          <span>{t('themes:colorHslaWithValue', { hsla: hslaString })}</span>
        </div>
      </div>
    </Item>
  );
};
